import type { ReactNode } from 'react';
import { useGT } from 'gt-react';
import { XIcon } from '@phosphor-icons/react';
import { formatCurrency } from '@erp/shared-utils';
import type { OrderReadiness } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { OrderReadinessButton } from './OrderReadiness';
import type { LineTotals } from '../utils/lineTotals';
import { formatLbs } from '../utils/lineWeight';

interface CartDetailHeaderProps {
  /** Vendor name (PO) or source warehouse (TO). */
  title: string;
  shipToName: string;
  kind: 'po' | 'to';
  /** Every line in the cart, approved or not. */
  lineCount: number;
  /**
   * The order's readiness, judged by the shared rule on the cart's Orders-table
   * row (GEN-3234) so it reads the same here as in the Order column. Null for
   * TOs and while the row loads.
   */
  readiness?: OrderReadiness | null;
  /** Totals for the approved lines, what Release sends (GEN-3230). */
  approved: LineTotals;
  /** Forward-buy note (GEN-2874), right after the readiness it qualifies. */
  forwardBuy?: ReactNode;
  /** Unsent draft POs for this vendor × ship-to (GEN-2872), after readiness. */
  openOrders?: ReactNode;
  /** Receive-by date control (GEN-2831); rendered before the actions. */
  receiveBy?: ReactNode;
  /** This cart's own controls, e.g. Release, before the close button. */
  actions?: ReactNode;
  onClose: () => void;
  /**
   * Sits inside the Detail section's bar, under the open order's Orders row:
   * no border, no wrapping, and a quieter title since that row names it too.
   */
  embedded?: boolean;
}

/**
 * The open order's one-line header: `Vendor → SHIPTO · PO`, what is approved
 * (lines, dollars, qty, weight), the readiness, then Release and close. The
 * Orders row above already carries the whole order's figures. 12px, no card.
 */
export function CartDetailHeader({
  title,
  shipToName,
  kind,
  lineCount,
  approved,
  readiness = null,
  forwardBuy,
  openOrders,
  receiveBy,
  actions,
  onClose,
  embedded = false,
}: CartDetailHeaderProps) {
  const gt = useGT();
  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-x-3 text-xs',
        embedded
          ? 'h-7 overflow-hidden'
          : 'min-h-8 flex-wrap gap-y-1 border-b py-1',
      )}
    >
      <HeaderTitle
        title={title}
        shipToName={shipToName}
        kind={kind}
        compact={embedded}
      />
      <ApprovedFigures totals={approved} lineCount={lineCount} />
      {readiness && <OrderReadinessButton showLabel readiness={readiness} />}
      {forwardBuy}
      {openOrders}
      {/* Never shrinks: the title and figures give way so Release stays. */}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {receiveBy}
        {actions}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onClose}
          aria-label={gt('Close detail', { context: 'detail header close' })}
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/** `Approved · 1 of 2 lines · $5,319.12 · Qty 44 · Wt —`. */
function ApprovedFigures({
  totals,
  lineCount,
}: {
  totals: LineTotals;
  lineCount: number;
}) {
  const gt = useGT();
  return (
    <div
      role="group"
      aria-label={gt('Approved', {
        context: 'detail header: the approved lines Release sends',
      })}
      className="flex min-w-0 items-center gap-3 tabular-nums"
    >
      <span className="shrink-0 font-medium">
        {gt('Approved', {
          context: 'detail header: the approved lines Release sends',
        })}
      </span>
      <span className="shrink-0">
        {gt('{approved} of {count} lines', {
          context: 'detail header: approved lines out of all lines in the cart',
          approved: totals.lineCount.toLocaleString(),
          count: lineCount.toLocaleString(),
        })}
      </span>
      <span className="shrink-0 font-medium">
        {formatCurrency(totals.totalCost)}
      </span>
      <ApprovedExtras totals={totals} />
    </div>
  );
}

/** Quantity and weight of the approved lines, quieter than the money. */
function ApprovedExtras({ totals }: { totals: LineTotals }) {
  const gt = useGT();
  return (
    <span className="min-w-0 truncate tabular-nums text-muted-foreground">
      {gt('Qty', { context: 'detail header approved row, total quantity' })}{' '}
      {totals.totalQuantity.toLocaleString()}
      {' · '}
      <span
        title={
          totals.unweighedLineCount > 0
            ? gt(
                '{count, plural, =1 {# line has no weight on file and is not counted} other {# lines have no weight on file and are not counted}}',
                {
                  context: 'detail header approved row, weight tooltip',
                  count: totals.unweighedLineCount,
                },
              )
            : undefined
        }
      >
        {gt('Wt', { context: 'detail header approved row, total weight' })}{' '}
        {totals.totalWeightLbs == null ? '—' : formatLbs(totals.totalWeightLbs)}
      </span>
    </span>
  );
}

/**
 * `Vendor → SHIPTO · PO`. Compact (embedded) it is muted and capped, so it
 * names the order without competing with the Orders row above (GEN-3230).
 */
function HeaderTitle({
  title,
  shipToName,
  kind,
  compact,
}: Pick<CartDetailHeaderProps, 'title' | 'shipToName' | 'kind'> & {
  compact: boolean;
}) {
  const gt = useGT();
  const name = compact ? 'truncate' : 'truncate font-medium';
  return (
    <div
      title={`${title} → ${shipToName}`}
      className={cn(
        'flex h-7 min-w-0 items-center gap-1.5',
        compact && 'max-w-64 text-muted-foreground',
      )}
    >
      <span className={name}>{title}</span>
      <span className="text-muted-foreground" aria-hidden>
        →
      </span>
      <span className={name}>{shipToName}</span>
      <span className="shrink-0 text-muted-foreground">
        {kind === 'po'
          ? gt('PO', { context: 'detail header order type' })
          : gt('TO', { context: 'detail header order type' })}
      </span>
    </div>
  );
}
